import React, { useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import { agentsData } from "Stores/agentsData";
import {
  AgentOrb,
  RunTicks,
  guardrailLine,
  metricsFor,
  needsIcp,
  orbMode,
  timeAgo,
  whenLabel
} from "Pages/Dashboard/agents/AgentCard";
import { daysOf, scheduleText } from "Pages/Dashboard/agents/DayOfWeekField";
import "Pages/Dashboard/agents/agents.scss";

/**
 * An agent in the copilot, drawn as the agents page draws it (.ag-acard): orb, name and what it
 * does, a status line, three numbers, the last ten runs and the next action. Live data and every
 * action go through agentsData (the logged-in session), so a click refreshes the overview and
 * every card for that agent updates in place. Clicking the card opens the agent side panel.
 */

export const scheduleOf = a => {
  const t = a.trigger || {};
  if (t.type === "schedule.recurring") {
    const cfg = t.config || {};
    const s = scheduleText(cfg.frequency, cfg.time, daysOf(cfg), (a.config && a.config.timezone) || "UTC");
    return `Runs ${s}`;
  }
  if (t.type && t.type.startsWith("event.")) return `When ${t.type.slice(6).replace(/_/g, " ").toLowerCase()}`;
  return "Runs when you start it";
};

/** Summaries can quote a saved-search name that carries a dash ("X — search"); read it plainly. */
export const plain = t => String(t || "").replace(/\s+[\u2014\u2013]\s+/g, " ");

/** Why it can't be switched on yet, or "" (same rules as the agent page checklist). */
export const notReady = a => {
  if (needsIcp(a)) return "Set who it should look for first.";
  if (!(a.flow || []).length) return "It has no steps yet.";
  return "";
};

/** The actions both the card and the panel offer. Returns the saved result, refreshes the cards. */
export const runAgentAction = async (kind, agentId) => {
  let out = null;
  if (kind === "test") out = await agentsData.testRun(agentId);
  if (kind === "run") out = await agentsData.startRun(agentId);
  if (kind === "on") out = await agentsData.setStatus(agentId, "ACTIVE");
  if (kind === "pause") out = await agentsData.setStatus(agentId, "PAUSED");
  await agentsData.fetchOverview();
  return out;
};

/** The overview card for an agent, fresh when the overview has it, else what the block carried. */
export const liveCard = (agentId, fallback) =>
  ((agentsData.overview && agentsData.overview.agents) || []).find(c => c.agent && c.agent.id === agentId) || fallback;

const STATE_LABEL = {
  RUNNING: "Running", WAITING_APPROVAL: "Needs you", FAILING: "Failing",
  ACTIVE: "Live", PAUSED: "Paused", DRAFT: "Draft", ARCHIVED: "Archived"
};

const CopilotAgentCard = observer(({ card, showOpenPage = false }) => {
  const history = useHistory();
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState(null);
  const [note, setNote] = useState("");
  if (!card || !card.agent) return null;
  const a = card.agent;
  const state = card.state || a.status || "DRAFT";
  const draft = a.status === "DRAFT";
  const metrics = metricsFor(a, card.totals);

  const act = kind => async e => {
    e.stopPropagation();
    if (busy) return;
    if (kind === "on" && notReady(a)) {
      setNote(notReady(a));
      return;
    }
    setBusy(kind);
    setNote("");
    try {
      const out = await runAgentAction(kind, a.id);
      if (kind === "test" && out) {
        setTest({ runId: out.id, add: out.enrolledCount || 0, skip: out.skippedCount || 0, credits: out.creditsUsed || 0 });
      }
    } finally {
      setBusy("");
    }
  };

  let line;
  if (state === "RUNNING") line = "Running now";
  else if (state === "WAITING_APPROVAL") line = `${card.waitingContacts || 0} contacts waiting for your approval`;
  else if (state === "FAILING") line = `Last ${card.failedInARow || 2} runs failed`;
  else if (a.status === "ACTIVE") line = card.nextRunAt ? `Next run ${whenLabel(card.nextRunAt)}` : "Live";
  else if (a.status === "PAUSED") line = "Paused · nothing runs until you resume";
  else line = notReady(a) || "Not switched on yet";

  return (
    <article
      className={`ag-acard ag-acard--${state.toLowerCase()} cpAgentCard`}
      onClick={() => copilotStore.openAgentPanel(a.id)}
    >
      <span className="ag-acard__accent" />
      <div className="ag-acard__head">
        <AgentOrb mode={draft && state !== "RUNNING" ? "draft" : orbMode(state)} />
        <div className="ag-acard__who">
          <span className="ag-acard__name" title={a.name}>{a.name || "Untitled agent"}</span>
          {a.summary && <span className="ag-acard__what" title={plain(a.summary)}>{plain(a.summary)}</span>}
        </div>
        <span className={`cpAgentCard__pill cpAgentCard__pill--${state.toLowerCase()}`}>{STATE_LABEL[state] || "Draft"}</span>
      </div>

      <div className="ag-acard__line">
        {(state === "ACTIVE" || state === "RUNNING") && <span className="ag-acard__linedot" />}
        <span>{line}{card.lastRun && !draft ? ` · last run ${timeAgo(card.lastRun.startedAt)}` : ""}</span>
      </div>

      <div className="ag-acard__nums">
        {metrics.map(m => (
          <div key={m.l} className="ag-acard__num"><b>{m.v}</b><span>{m.l}</span></div>
        ))}
        <span className="ag-acard__window">last 14 days</span>
      </div>

      <div className="ag-acard__foot1">
        {!draft && <RunTicks runs={card.recentRuns} />}
        <span className="ag-acard__sched">{scheduleOf(a)}</span>
      </div>

      {test && (
        <div className="cpAgentCard__test" onClick={e => e.stopPropagation()}>
          <i className="material-icons-outlined">visibility</i>
          <span>Test run: would add <b>{test.add}</b>, skip {test.skip}, about {test.credits} credits. Nothing was changed.</span>
          {test.runId && (
            <button type="button" className="ag-link" onClick={() => history.push(`/agents/${a.id}/runs/${test.runId}`)}>
              See who<i className="material-icons-outlined">arrow_forward</i>
            </button>
          )}
        </div>
      )}
      {note && <div className="cpAgentCard__note">{note}</div>}

      <div className="ag-acard__foot">
        <span className="ag-acard__guard">{draft ? "Created as a draft" : guardrailLine(a)}</span>
        <div className="cpAgentCard__actions">
          {(draft || a.status === "PAUSED") && (
            <button type="button" className="ag-btn ag-btn--sm" disabled={!!busy} onClick={act("test")}>
              <i className="material-icons-outlined">visibility</i>{busy === "test" ? "Testing…" : "Test run"}
            </button>
          )}
          {draft && (
            <button type="button" className="ag-btn ag-btn--sm ag-btn--primary" disabled={!!busy} onClick={act("on")}>
              <i className="material-icons-outlined">power_settings_new</i>{busy === "on" ? "Turning on…" : "Turn on"}
            </button>
          )}
          {a.status === "PAUSED" && (
            <button type="button" className="ag-btn ag-btn--sm ag-btn--primary" disabled={!!busy} onClick={act("on")}>
              <i className="material-icons-outlined">play_arrow</i>{busy === "on" ? "Resuming…" : "Resume"}
            </button>
          )}
          {a.status === "ACTIVE" && (
            <button type="button" className="ag-btn ag-btn--sm" disabled={!!busy} onClick={act("run")}>
              <i className="material-icons-outlined">play_arrow</i>{busy === "run" ? "Starting…" : "Run now"}
            </button>
          )}
          {showOpenPage && (
            <button type="button" className="ag-btn ag-btn--sm" onClick={e => { e.stopPropagation(); history.push(`/agents/${a.id}`); }}>
              <i className="material-icons-outlined">open_in_new</i>Open agent
            </button>
          )}
        </div>
      </div>
    </article>
  );
});

export default CopilotAgentCard;
